import { AlertTriangle, Info, Languages, Layers, Sparkles } from 'lucide-react';
import { LANGUAGE_OPTIONS, RECOGNITION_MODES } from '../../lib/ocr/constants';
import { ENGINES } from '../../lib/ocr/engines/routing';

const LANGUAGE_TESTIDS = {
  english: 'language-select-english',
  hindi: 'language-select-hindi',
  sanskrit: 'language-select-sanskrit',
  devanagari: 'language-select-devanagari',
  mixed: 'language-select-mixed',
};

const SectionShell = ({ id, icon: Icon, title, description, children, testId }) => (
  <section aria-labelledby={`${id}-heading`} className="surface-card p-5 sm:p-6" data-testid={testId} id={id}>
    <div className="mb-4 flex items-start gap-3">
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#eef0fe] text-indigo-700">
        <Icon aria-hidden="true" className="h-4.5 w-4.5" />
      </span>
      <div>
        <h2 className="font-display text-base font-bold text-slate-900 md:text-lg" id={`${id}-heading`}>
          {title}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{description}</p>
      </div>
    </div>
    {children}
  </section>
);

export const Controls = ({
  languageId,
  onLanguage,
  requestedEngine,
  activeEngine,
  onEngine,
  routing,
  nextGenHidden,
  nextGenBlocker,
  nextGenReady,
  mode,
  onMode,
  disabled,
}) => (
  <div className="grid gap-4 lg:grid-cols-3">
    <SectionShell
      id="language"
      icon={Languages}
      testId="language-selector"
      title="1 · Language"
      description="Pick the script that appears in your image. English is the default. Hindi + Sanskrit keeps English out of the way for pure Devanagari pages."
    >
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
        {LANGUAGE_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            data-testid={LANGUAGE_TESTIDS[option.id]}
            data-active={languageId === option.id}
            aria-pressed={languageId === option.id}
            disabled={disabled}
            onClick={() => onLanguage(option.id)}
            className="segment-btn text-sm font-semibold disabled:opacity-60"
          >
            {option.label}
            <span
              className={`mt-0.5 block text-xs font-medium ${
                languageId === option.id ? 'text-indigo-100' : 'text-slate-500'
              }`}
            >
              Tesseract: {option.tesseract.join(' + ')}
            </span>
          </button>
        ))}
      </div>
    </SectionShell>

    <SectionShell
      id="engine"
      icon={Sparkles}
      testId="engine-selector"
      title="2 · OCR engine"
      description="Classic handles every supported script. Next-Gen is English-only here."
    >
      <div className="grid gap-2">
        {ENGINES.filter((engine) => !(engine.id === 'nextgen' && nextGenHidden)).map((engine) => {
          const isDisabled =
            disabled || (engine.id === 'nextgen' && routing.nextGenDisabled);
          return (
            <button
              key={engine.id}
              type="button"
              data-testid={engine.id === 'classic' ? 'engine-select-tesseract' : 'engine-select-paddle'}
              data-active={activeEngine === engine.id}
              aria-pressed={activeEngine === engine.id}
              aria-describedby={engine.id === 'nextgen' && routing.nextGenDisabled ? 'nextgen-notice' : undefined}
              disabled={isDisabled}
              onClick={() => onEngine(engine.id)}
              className="segment-btn text-sm font-semibold"
            >
              {engine.label}
              <span
                className={`mt-0.5 block text-xs font-medium ${
                  activeEngine === engine.id ? 'text-indigo-100' : 'text-slate-500'
                }`}
              >
                {engine.tagline}
              </span>
            </button>
          );
        })}
      </div>

      {routing.notice && !nextGenHidden ? (
        <p
          className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800"
          data-testid="nextgen-language-notice"
          id="nextgen-notice"
        >
          <Info aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {routing.notice}
        </p>
      ) : null}

      {nextGenReady && requestedEngine === 'nextgen' ? (
        <p className="mt-3 text-xs font-medium text-emerald-700" data-testid="nextgen-ready-note">
          PP-OCRv5 models loaded and cached by your browser.
        </p>
      ) : null}

      {nextGenBlocker ? (
        <div
          className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="nextgen-blocker-note"
        >
          <p className="flex items-start gap-2 font-semibold">
            <AlertTriangle aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Next-Gen OCR is unavailable in this browser. Classic OCR is being used instead.
          </p>
          <p className="mt-1 break-words font-mono text-[11px] leading-relaxed text-amber-800">
            {nextGenBlocker.technical}
          </p>
        </div>
      ) : null}
    </SectionShell>

    <SectionShell
      id="mode"
      icon={Layers}
      testId="mode-selector"
      title="3 · Recognition mode"
      description="Choose the layout that matches your image."
    >
      <div className="grid gap-2">
        {RECOGNITION_MODES.map((option) => (
          <button
            key={option.id}
            type="button"
            data-testid={option.id === 'document' ? 'mode-select-document' : 'mode-select-poster'}
            data-active={mode === option.id}
            aria-pressed={mode === option.id}
            disabled={disabled}
            onClick={() => onMode(option.id)}
            className="segment-btn text-sm font-semibold disabled:opacity-60"
          >
            {option.label}
            <span
              className={`mt-0.5 block text-xs font-medium ${
                mode === option.id ? 'text-indigo-100' : 'text-slate-500'
              }`}
            >
              {option.hint}
            </span>
          </button>
        ))}
      </div>
    </SectionShell>
  </div>
);

export default Controls;
